import { useState } from 'react';
import { Check, Copy, Eye, EyeOff, Info, Loader2, RefreshCw, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useWorkspace } from '@/contexts/workspace-context';
import { useChannelToken, useRotateChannelToken } from '@/components/channels/queries';

/**
 * Channel-detail "Access Token" row (shared by the WhatsApp + Instagram bodies).
 *
 * In the gateway model HookMyApp holds the real Meta token and NEVER exposes it.
 * The customer authenticates to the HookMyApp gateway with a HookMyApp-issued
 * bearer token (`hmat_live_…`) dropped into `Authorization: Bearer` in place of
 * the Meta token. That bearer is THE customer's access token — it is stored
 * encrypted at rest and returned in full here so they can reveal and copy it any
 * time (an access token, not a write-once API key).
 *
 * A connected channel is born with its access token, so one normally exists; the
 * only token-less case is the one-time post-migration reconnect window (a
 * pre-existing channel that hasn't reconnected yet), surfaced here as a reconnect
 * prompt on `CHANNEL_ACCESS_TOKEN_MISSING`. Viewing + copying the token is
 * available to ANY workspace member — it is the credential they need to operate
 * the channel and drive the CLI, so `GET /meta/channels/:id/token` is
 * membership-level. Only **rotating** it (revoke-then-mint, which breaks every
 * live integration) is admin-gated: the rotate control here and the rotate
 * endpoint require workspace admin (`canEdit`). Renders a fragment of two grid
 * cells (label + value) to slot into the parent `grid-cols-[200px_1fr]` body.
 *
 * The read lives in the Query cache keyed per workspace+channel, so a channel
 * switch can never clobber another channel's token state (the old manual
 * request-id guard is obsolete) and the GET stays suppressGlobalError — the
 * widget fails soft with its own inline error + Retry.
 */
export function AccessTokenField({ channelId, canEdit }: { channelId: string; canEdit: boolean }) {
  const { activeWorkspace, sessionScoped } = useWorkspace();
  const tokenQuery = useChannelToken(activeWorkspace?.id, channelId, sessionScoped);
  const rotate = useRotateChannelToken(activeWorkspace?.id);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const tokenInfo = tokenQuery.data ?? null;
  const loading = tokenQuery.isPending || tokenQuery.isFetching;
  const rotating = rotate.isPending;
  // Only CHANNEL_ACCESS_TOKEN_MISSING is the legit reconnect-window state
  // (a disabled channel's token is revoked). Every other failure is an
  // anomaly: a connected channel ALWAYS has a token, so we surface it as an
  // error with Retry, never as a calm "no token" message.
  const missing = tokenQuery.error?.code === 'CHANNEL_ACCESS_TOKEN_MISSING';
  const unavailable = tokenQuery.isError && !missing;

  async function handleCopy() {
    if (!tokenInfo?.token) return;
    try {
      await navigator.clipboard.writeText(tokenInfo.token);
      setCopied(true);
      toast.success('Access token copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy. Reveal it and copy manually.');
    }
  }

  function handleRotate() {
    rotate.mutate(
      { channelId },
      {
        onSuccess: () => {
          // Auto-reveal the freshly rotated secret so the user can copy it
          // straight into their integration without a second click. The hook
          // seeds the new token into the query cache, which also replaces any
          // missing/unavailable state.
          setRevealed(true);
          setConfirmOpen(false);
          toast.success('Access token rotated');
        },
        onError: () => toast.error('Could not rotate the access token. Try again.'),
      },
    );
  }

  return (
    <>
      <span className="flex items-center gap-1 text-muted-foreground">
        <span>Access Token</span>
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label="What is the access token?"
                className="inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
              />
            }
          >
            <Info className="size-3.5" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            Your HookMyApp gateway token. Use it to authenticate calls to the
            HookMyApp gateway in place of your Meta token.
          </TooltipContent>
        </Tooltip>
      </span>
      <div>
        {loading ? (
          <Loader2
            className="size-3.5 animate-spin text-muted-foreground"
            aria-label="Loading access token"
          />
        ) : tokenInfo ? (
          <span className="flex items-center gap-1">
            <span className="font-mono text-xs break-all">
              {revealed ? tokenInfo.token : `${tokenInfo.tokenPrefix}…${tokenInfo.tokenSuffix}`}
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setRevealed((r) => !r)}
              aria-label={revealed ? 'Hide access token' : 'Reveal access token'}
              title={revealed ? 'Hide' : 'Reveal'}
            >
              {revealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleCopy}
              aria-label="Copy access token"
              title="Copy"
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            </Button>
            {canEdit && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => setConfirmOpen(true)}
                aria-label="Rotate access token"
                title="Rotate"
              >
                <RotateCw className="size-3.5" />
              </Button>
            )}
          </span>
        ) : missing ? (
          <span className="text-xs text-muted-foreground">
            Reconnect to generate your access token
          </span>
        ) : unavailable ? (
          // Not a normal state: a connected channel always has a token. Reaching
          // here is an anomaly, so surface it as an error with Retry, not a calm
          // "unavailable" message.
          <span className="flex items-center gap-2 text-xs text-destructive">
            Couldn't load access token.
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => void tokenQuery.refetch()}
              aria-label="Retry loading access token"
              title="Retry"
            >
              <RefreshCw className="size-3.5" />
            </Button>
          </span>
        ) : null}
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(next) => {
          if (!rotating) setConfirmOpen(next);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate access token?</AlertDialogTitle>
            <AlertDialogDescription>
              Your current token stops working immediately. Update your integration
              with the new token.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rotating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={rotating}
              onClick={handleRotate}
            >
              {rotating && <Loader2 className="size-3.5 animate-spin" />}
              {rotating ? 'Rotating...' : 'Rotate Token'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
